import { FunctionFragment, Interface } from 'ethers'
import { CHAIN_ID } from '../lib/constants.js'
import {
  formatAddressName,
  formatAmount,
  formatDate,
  formatEnum,
  formatRaw,
  formatTokenAmount,
  formatUnit,
  type DateEncoding,
} from './format.js'
import type { Erc7730Descriptor, Erc7730Field, FormatName } from './types.js'

/**
 * Resolves calldata against safeward's vendored ERC-7730 descriptors
 * (./registry/**, populated by scripts/sync-erc7730.mjs) into a human-readable
 * "clear signing" view. Synchronous and fails safe throughout: any unresolvable
 * field, unknown token, or decode mismatch degrades to a plain/raw value or a
 * `null` result rather than a guessed or confidently-wrong render - this feeds a
 * fund-custody review UI, and a misleading "verified" label is worse than none.
 */

// Descriptors are bundled at build time, not fetched at runtime - see the
// vendoring rationale in scripts/sync-erc7730.mjs and the plan this implements.
const modules = import.meta.glob('./registry/**/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, Erc7730Descriptor>

function buildIndex(): Map<string, Erc7730Descriptor> {
  const map = new Map<string, Erc7730Descriptor>()
  for (const descriptor of Object.values(modules)) {
    const deployments = descriptor.context?.contract?.deployments ?? []
    for (const dep of deployments) {
      if (dep.chainId !== CHAIN_ID) continue
      map.set(dep.address.toLowerCase(), descriptor)
    }
  }
  return map
}

const descriptorIndex = buildIndex()

export interface ClearSigningField {
  label: string
  value: string
  format: FormatName | 'none'
}

export interface ClearSigningResult {
  contractName: string
  owner?: string
  intent: string
  functionName: string
  signature: string
  fields: ClearSigningField[]
}

/** Resolves a '$.'-prefixed reference into the descriptor object itself, e.g.
 *  "$.metadata.constants.USDYaddress" or "$.metadata.enums.interestRateMode".
 *  Returns undefined if any path segment is missing - never throws. */
function resolveDescriptorRef(descriptor: Erc7730Descriptor, ref: string): unknown {
  const parts = ref.slice(2).split('.')
  let value: unknown = descriptor
  for (const part of parts) {
    if (value && typeof value === 'object' && part in (value as Record<string, unknown>)) {
      value = (value as Record<string, unknown>)[part]
    } else {
      return undefined
    }
  }
  return value
}

/** `params.token`/`params.threshold`/`params.$ref` etc. are either a literal
 *  value or a '$.'-prefixed reference into the descriptor - resolve either form. */
function resolveMaybeRef(descriptor: Erc7730Descriptor, value: unknown): unknown {
  return typeof value === 'string' && value.startsWith('$.')
    ? resolveDescriptorRef(descriptor, value)
    : value
}

/** The '@' root names values from the container wrapping the structured data -
 *  the EVM transaction itself, not the decoded calldata. Only `to`/`chainId`/
 *  `value` are resolvable here: `@.from` (the tx sender) would need wallet/
 *  proposer context resolveClearSigning doesn't have (and for a Module Safe's
 *  wrapped inner call, "from" is the Safe executing it, not the human proposer -
 *  guessing wrong is worse than leaving it unresolved). Fields referencing
 *  `@.from` degrade to their raw fallback like any other unresolved path. */
interface Container {
  to: string
  value?: string
  chainId: number
}

function resolveContainerPath(container: Container, key: string): unknown {
  switch (key) {
    case 'to':
      return container.to
    case 'value':
      return container.value
    case 'chainId':
      return container.chainId
    default:
      return undefined
  }
}

interface PathContext {
  descriptor: Erc7730Descriptor
  pathValues: Record<string, unknown>
  container: Container
}

/** Resolves a field `path` (or a path-valued param like `tokenPath`) per the
 *  three root prefixes the schema defines: '$.' into the descriptor itself,
 *  '@.' into the transaction container, '#.' (or no prefix - the implicit
 *  default root) into the decoded function arguments. */
function resolvePath(path: string, ctx: PathContext): unknown {
  if (path.startsWith('$.')) return resolveDescriptorRef(ctx.descriptor, path)
  if (path.startsWith('@.')) return resolveContainerPath(ctx.container, path.slice(2))
  const key = path.startsWith('#.') ? path.slice(2) : path
  return ctx.pathValues[key]
}

interface FunctionMatch {
  signature: string
  fields: Erc7730Field[]
  intent?: string
  interpolatedIntent?: string
  fragmentId: string
  fragment: FunctionFragment
  iface: Interface
}

function findMatchingFunction(descriptor: Erc7730Descriptor, selector: string): FunctionMatch | null {
  for (const [signature, format] of Object.entries(descriptor.display.formats)) {
    let fragment: FunctionFragment
    let iface: Interface
    try {
      fragment = FunctionFragment.from(signature)
      iface = new Interface([fragment])
    } catch {
      continue
    }
    if (fragment.selector.toLowerCase() !== selector) continue
    return {
      signature,
      fields: format.fields ?? [],
      intent: format.intent,
      interpolatedIntent: format.interpolatedIntent,
      fragmentId: format.$id,
      fragment,
      iface,
    }
  }
  return null
}

/** Fills `{fieldPath}` placeholders from already-formatted field values. Returns
 *  null (not a string with literal `{...}` left in it) if any reference can't be
 *  resolved, so callers can fall back to the plain intent instead. */
function fillIntent(template: string, formattedByPath: Record<string, string>): string | null {
  let missing = false
  const filled = template.replace(/\{([^}]+)\}/g, (_match, key: string) => {
    const value = formattedByPath[key.trim()]
    if (value === undefined) {
      missing = true
      return ''
    }
    return value
  })
  return missing ? null : filled
}

/** Descriptor-authored numeric strings (e.g. an unlimited-approval threshold)
 *  show up as either decimal ("115792...") or hex ("0xffff...") - BigInt()
 *  parses both natively. Returns undefined rather than throwing on anything else. */
function toBigIntOrUndefined(value: unknown): bigint | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined
  try {
    return BigInt(value)
  } catch {
    return undefined
  }
}

function formatField(field: Erc7730Field, rawValue: unknown, ctx: PathContext): string {
  const { descriptor } = ctx
  const params = field.params ?? {}
  switch (field.format) {
    case 'addressName':
      return formatAddressName(rawValue)

    case 'tokenAmount': {
      // `token` is a constant-or-descriptor-reference (resolveMaybeRef);
      // `tokenPath` is a path INTO structured data (resolvePath) - e.g. USDT's
      // approve() uses `tokenPath: "@.to"` since the token being approved IS
      // the contract being called, not one of its decoded arguments.
      const resolvedToken =
        params.token !== undefined
          ? resolveMaybeRef(descriptor, params.token)
          : typeof params.tokenPath === 'string'
            ? resolvePath(params.tokenPath, ctx)
            : undefined
      const threshold = toBigIntOrUndefined(resolveMaybeRef(descriptor, params.threshold))
      return formatTokenAmount(rawValue, typeof resolvedToken === 'string' ? resolvedToken : null, {
        threshold,
        message: typeof params.message === 'string' ? params.message : undefined,
      })
    }

    case 'amount':
      return formatAmount(rawValue)

    case 'date':
      return formatDate(
        rawValue,
        typeof params.encoding === 'string' ? (params.encoding as DateEncoding) : undefined,
      )

    case 'unit':
      return formatUnit(rawValue, {
        base: typeof params.base === 'string' ? params.base : '',
        decimals: typeof params.decimals === 'number' ? params.decimals : undefined,
        prefix: typeof params.prefix === 'boolean' ? params.prefix : undefined,
      })

    case 'enum': {
      const enumMap = resolveMaybeRef(descriptor, params.$ref)
      return formatEnum(rawValue, enumMap && typeof enumMap === 'object' ? (enumMap as Record<string, string>) : undefined)
    }

    // 'calldata' (nested/multicall decoding), 'nftName', 'duration', 'raw', and
    // no-format-specified all degrade to the plain decoded value in v1 - see the
    // implementation plan's phasing for why 'calldata' recursion is out of scope.
    default:
      return formatRaw(rawValue)
  }
}

export function resolveClearSigning(
  to: string | undefined,
  data: string | undefined,
  context: { value?: string } = {},
): ClearSigningResult | null {
  try {
    if (!to || !data || data.length < 10) return null
    const descriptor = descriptorIndex.get(to.toLowerCase())
    if (!descriptor) return null

    const selector = data.slice(0, 10).toLowerCase()
    const match = findMatchingFunction(descriptor, selector)
    if (!match) return null

    let decoded
    try {
      decoded = match.iface.decodeFunctionData(match.fragment, data)
    } catch {
      return null
    }

    const pathValues: Record<string, unknown> = {}
    match.fragment.inputs.forEach((input, i) => {
      if (input.name) pathValues[input.name] = decoded[i]
    })

    const ctx: PathContext = {
      descriptor,
      pathValues,
      container: { to, value: context.value, chainId: CHAIN_ID },
    }

    const fields: ClearSigningField[] = []
    const formattedByPath: Record<string, string> = {}

    for (const field of match.fields) {
      if (field.visible === 'never') continue
      const rawValue = resolvePath(field.path, ctx)
      const formatted = formatField(field, rawValue, ctx)
      formattedByPath[field.path] = formatted
      fields.push({ label: field.label ?? field.path, value: formatted, format: field.format ?? 'none' })
    }

    const interpolated = match.interpolatedIntent ? fillIntent(match.interpolatedIntent, formattedByPath) : null
    const intent = interpolated ?? match.intent ?? match.fragmentId

    return {
      contractName: descriptor.metadata.contractName ?? descriptor.context.$id,
      owner: descriptor.metadata.owner,
      intent,
      functionName: match.fragment.name,
      signature: match.signature,
      fields,
    }
  } catch {
    return null
  }
}
